import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Send,
  Sparkles,
  Trash2,
  Settings,
  Square,
  RefreshCw,
  ChevronDown,
  MoreHorizontal,
  Cpu,
  Gauge,
  Quote,
} from 'lucide-react';
import { streamChat } from '../../api/chat.js';
import VoiceRecorder from './VoiceRecorder.jsx';
import { useMusic } from '../musicbox/MusicContext.jsx';
import {
  fetchMessages,
  pushMessage,
  deleteMessage as apiDeleteMessage,
  clearAllMessages,
  truncateFrom,
} from '../../api/messages.js';
import {
  fetchConversations,
  createConversation,
  renameConversation,
  setConversationArchived,
  deleteConversation,
} from '../../api/conversations.js';
import {
  ensureBootstrap,
  loadCharacters,
  getActiveId,
  buildSystemPrompt,
} from '../../lib/characterCards.js';
import {
  ensurePresetsBootstrap,
  loadPresets,
  getActivePresetId,
} from '../../lib/presets.js';
import { loadEntries, matchLorebook } from '../../lib/lorebook.js';
import { processImage, buildContentArray } from '../../lib/image.js';
import {
  loadWallpaper,
  saveWallpaperPatch,
  sampleImageTopColor,
  saveTopRgb,
  loadTopRgb,
  wallpaperImgStyle,
  DEFAULT_POS,
} from '../../lib/wallpaper.js';
import { refreshChromeColor } from '../../lib/theme.js';
import { SKIN_EVENT } from '../../lib/themeSkin.js';
import { installBubbleFlow } from '../../lib/bubbleFlow.js';
import { useFlag } from '../../lib/flags.js';
import { pushActivity } from '../../lib/activityLog.js';
import { saelState } from '../../components/Crab.jsx';
import SettingsPanel from './SettingsPanel.jsx';
import Message, { ImageLightbox } from './Message.jsx';
import ConversationsDrawer from './ConversationsDrawer.jsx';
import CcModelControl from './CcModelControl.jsx';
import {
  STORAGE_PROVIDER_KEY,
  STORAGE_PROVIDER_BASE,
  STORAGE_PROVIDER_MODEL,
  STORAGE_CURRENT_CONV,
  STORAGE_DEEP_MODE,
  daysTogether,
  nowLocalStr,
  appendTimeContext,
  appendChatAbilities,
  buildQuotePrefix,
  contentText,
  extractReactions,
  nextId,
  nextConvId,
  fmtMonthDay,
} from './chatUtils.js';
import { ImagePlus, X } from 'lucide-react';
import './Chat.css';

/* ============================================================
   línkè · 聊天模块（2026-05-26 多对话线）
   - 每条对话强绑定一个角色卡 (conv.character_id)，换角色 = 新建对话
   - currentConvId 决定 messages 拉哪条线 + 用哪个角色卡 system prompt
   - 顶部 "聊 天" 标题点开下拉抽屉切换 / 新建 / 改名 / 归档 / 删除
   ============================================================ */

export default function Chat() {
  // 角色卡 / 预设 / 世界书（用户资产，独立于对话存在）
  const [characters, setCharacters] = useState(() => {
    ensureBootstrap();
    return loadCharacters();
  });
  const [defaultCharacterId, setDefaultCharacterId] = useState(() => getActiveId());
  const [presets, setPresets] = useState(() => {
    ensurePresetsBootstrap();
    return loadPresets();
  });
  const [activePresetId, setActivePresetId] = useState(() => getActivePresetId());
  const [lorebook, setLorebook] = useState(() => loadEntries());

  // 对话线
  const [conversations, setConversations] = useState([]);
  const [currentConvId, setCurrentConvId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bootError, setBootError] = useState(null);
  const music = useMusic(); // 「和爸爸一起听」：接住播放器发来的请求

  // 当前对话 + 强绑定的角色卡（从 conv.character_id 取，不从 settings）
  const currentConv = useMemo(
    () => conversations.find((c) => c.id === currentConvId) || null,
    [conversations, currentConvId],
  );
  const activeCharacter = useMemo(() => {
    const cid = currentConv?.character_id;
    if (!cid) return null;
    return characters.find((c) => c.id === cid) || null;
  }, [characters, currentConv]);
  const activePreset = useMemo(
    () => presets.find((p) => p.id === activePresetId) || null,
    [presets, activePresetId],
  );

  const [messages, setMessages] = useState([]);
  // 她刚发出去的那条消息 id：进场走 TG 式「从输入框弹上来」（2026-07-19 铃点单）；
  // 装载历史/切对话时清空，旧消息仍走轻淡入
  const [justSentId, setJustSentId] = useState(null);
  // 消息列表「进场时刻」（2026-07-19 铃点单：她的消息也要有进场动画）：
  // 刷新/切对话装载完那 1 秒挂 ch-list-enter，让底部几条错拍浮现；
  // 之后摘掉 → 平时新消息只淡入不排队，发消息手感不变钝。
  const [listEntering, setListEntering] = useState(false);
  useEffect(() => {
    if (!listEntering) return undefined;
    const t = setTimeout(() => setListEntering(false), 1000);
    return () => clearTimeout(t);
  }, [listEntering]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [messagesError, setMessagesError] = useState(null);
  const [input, setInput] = useState('');
  const [generating, setGenerating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // 聊天壁纸（每台设备自己的，见 lib/wallpaper.js）
  const [wallpaper, setWallpaper] = useState(loadWallpaper);
  /* 切主题时每个主题各换各的壁纸（lib/themeSkin.js）：localStorage 已被换过，
     但这里的 state 是挂载时读的一份缓存 → 收到广播重读一次。 */
  useEffect(() => {
    const reload = () => setWallpaper(loadWallpaper());
    window.addEventListener(SKIN_EVENT, reload);
    return () => window.removeEventListener(SKIN_EVENT, reload);
  }, []);
  // 本功能之前设的老壁纸没采样过顶部色 → 挂载时补采一次，让顶栏也无缝
  useEffect(() => {
    if (wallpaper.img && !loadTopRgb()) {
      sampleImageTopColor(wallpaper.img).then((rgb) => {
        saveTopRgb(rgb);
        refreshChromeColor();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // 迷你顶条：大标题滚出视野后淡入的 44px 毛玻璃条
  const [miniOn, setMiniOn] = useState(false);
  const [miniMenuOpen, setMiniMenuOpen] = useState(false);
  // ⋯ 菜单里「模型」那一项的展开态（把切模型收进菜单，不再占迷你条横向空间）
  const [miniModelExpand, setMiniModelExpand] = useState(false);
  // 「档位」那一项的展开态（思考投入档位，和模型放一起）
  const [miniEffortExpand, setMiniEffortExpand] = useState(false);

  // 深度模式后端 CC 模型切换（每台设备看到的是服务器同一份，走 /api/cc-model）
  const [ccModel, setCcModel] = useState(null);
  const [ccModelOptions, setCcModelOptions] = useState([]);
  const [ccModelBusy, setCcModelBusy] = useState(false);
  const [ccModelNote, setCcModelNote] = useState(null);
  // 思考投入档位切换（2026-09-04 铃点单，走 /api/cc-effort，同 GET /api/cc-model 一起拿）
  const [ccEffort, setCcEffort] = useState(null);
  const [ccEffortOptions, setCcEffortOptions] = useState([]);
  const [ccEffortBusy, setCcEffortBusy] = useState(false);

  // 图片：用户选了但还没发送
  const [pendingImage, setPendingImage] = useState(null); // {media_type, data, preview, bytes}
  const [imageError, setImageError] = useState(null);
  // 点消息里的图 → 应用内灯箱放大（2026-07-02，替代 window.open：iOS PWA 里那个会没反应）
  const [lightboxImg, setLightboxImg] = useState(null); // {media_type, data, fromRect?}
  // 点旅程卡 → 应用内全屏翻卡（2026-08-31 云旅行明信片，iframe 复用 journey.html）
  const [journeyUrl, setJourneyUrl] = useState(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  const [providerKey, setProviderKey] = useState(() => {
    try { return localStorage.getItem(STORAGE_PROVIDER_KEY) ?? ''; } catch { return ''; }
  });
  const [providerBase, setProviderBase] = useState(() => {
    try { return localStorage.getItem(STORAGE_PROVIDER_BASE) ?? ''; } catch { return ''; }
  });
  const [providerModel, setProviderModel] = useState(() => {
    try { return localStorage.getItem(STORAGE_PROVIDER_MODEL) ?? ''; } catch { return ''; }
  });
  // 深度模式开关（Sael本体 / Claude Code）
  const [deepMode, setDeepMode] = useState(() => {
    try { return localStorage.getItem(STORAGE_DEEP_MODE) === '1'; } catch { return false; }
  });
  // ② 斜杠命令开关（关 = 打 / 当普通消息发，跟以前一模一样）
  const [slashOn] = useFlag('slash_commands');
  // ③ 手机推送开关（关 = 不推；开 = 回复收完且你不在页面时推一条到手机）
  const [barkPush] = useFlag('bark_push');
  // ④ 活动实时窗开关（关 = 不往活动日志塞东西，零开销）
  const [activityOn] = useFlag('activity_window');
  // TG 三件套开关（2026-07-19 铃点单）：①拆气泡在 Message 里读，这里管 ③引用 ④小表情
  const [quoteOn] = useFlag('quote_reply');
  const [reactOn] = useFlag('sael_reactions');
  const [splitOn] = useFlag('split_bubbles');
  // ③ 引用回复：点气泡上的 “ 按钮 → 这里记下被引用那句 → 输入区浮出引用条
  const [quoteTarget, setQuoteTarget] = useState(null); // {id, role, text}

  // ④ Sael的小心情：把每条回复里的 [react:x] 归到它前面最近的一条铃的消息上。
  // 存库存的是带标记原文 → 这里每次渲染重算，刷新/换设备都不丢。
  const reactionsMap = useMemo(() => {
    if (!reactOn) return {};
    const map = {};
    let lastUserId = null;
    for (const m of messages) {
      if (m.pending || m.error) continue;
      if (m.role === 'user') {
        lastUserId = m.id;
      } else if (m.role === 'assistant' && lastUserId) {
        const { reactions } = extractReactions(contentText(m.content));
        if (reactions.length > 0) {
          map[lastUserId] = [...(map[lastUserId] || []), ...reactions];
        }
      }
    }
    return map;
  }, [messages, reactOn]);
  const abortRef = useRef(null);
  // 「开新一段」：点「新对话」时记下这条新对话的 id；它的第一条深度模式消息会带
  // reset_context，让后台克克开一个全新干净会话（只触发一次，随后清空）。
  const resetPendingConvRef = useRef(null);
  const listRef = useRef(null);
  const scrollRef = useRef(null);

  // 塔罗 × Sael 联动：从塔罗页点「让 Sael 解读」过来时，牌面已被存进
  // sessionStorage —— 取出来填进输入框（只填不发，铃确认后自己点 ✨）。
  useEffect(() => {
    try {
      const handoff = sessionStorage.getItem('lingke.tarot.handoff');
      if (handoff) {
        sessionStorage.removeItem('lingke.tarot.handoff');
        setInput(handoff);
        setTimeout(() => inputRef.current?.focus(), 300);
      }
    } catch {}
  }, []);

  // 输入框随内容自动增高：静止 1 行，最多长到 ~140px 再内部滚动
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }, [input]);

  /* ---------- bootstrap：拉 conversations + 选当前 ---------- */
  async function bootstrapConversations() {
    setBootError(null);
    try {
      let convs = await fetchConversations();
      let stored = null;
      try { stored = localStorage.getItem(STORAGE_CURRENT_CONV); } catch {}
      let pick =
        convs.find((c) => c.id === stored && !c.archived) ||
        convs.find((c) => !c.archived) ||
        convs[0];
      if (!pick) {
        // 一条对话都没有 → 自动建一条
        const newId = nextConvId();
        const charId = defaultCharacterId;
        const char = characters.find((c) => c.id === charId);
        const name = char ? `跟 ${char.name} · ${fmtMonthDay(new Date())}` : '新对话';
        await createConversation({ id: newId, name, character_id: charId });
        convs = await fetchConversations();
        pick = convs.find((c) => c.id === newId) || convs[0];
      }
      setConversations(convs);
      setCurrentConvId(pick.id);
      try { localStorage.setItem(STORAGE_CURRENT_CONV, pick.id); } catch {}
    } catch (e) {
      setBootError(e.message || '加载对话失败');
      setMessagesLoading(false);
    }
  }
  useEffect(() => {
    bootstrapConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- 切换/重新加载某条对话的消息 ---------- */
  // 防串线：快速切对话时，慢返回的旧请求不能覆盖新对话的消息。
  // 每次加载领一个递增序号，返回时序号已过期（说明又切走/又刷新了）就整体作废。
  const loadSeqRef = useRef(0);
  async function reloadMessages() {
    if (!currentConvId) return;
    const convId = currentConvId;
    const seq = ++loadSeqRef.current;
    const stale = () => seq !== loadSeqRef.current;
    setMessagesLoading(true);
    setMessagesError(null);
    try {
      let list = await fetchMessages(convId);
      // 一次性 legacy 迁移：server 是空 + localStorage 有旧消息 → 推进当前对话
      if (list.length === 0) {
        try {
          const legacy = localStorage.getItem('lingke.chat.messages.v1');
          if (legacy) {
            const legacyMsgs = JSON.parse(legacy);
            if (Array.isArray(legacyMsgs) && legacyMsgs.length > 0) {
              const base = Date.now() - legacyMsgs.length * 1000;
              for (let i = 0; i < legacyMsgs.length; i++) {
                const m = legacyMsgs[i];
                if (!m || (!m.content && !m.error)) continue;
                await pushMessage(
                  {
                    id: m.id || `legacy-${base + i}-${i}`,
                    role: m.role === 'assistant' ? 'assistant' : 'user',
                    content: m.content || '',
                    created_at: new Date(base + i * 1000).toISOString(),
                    error: m.error,
                  },
                  convId,
                );
              }
              localStorage.removeItem('lingke.chat.messages.v1');
              list = await fetchMessages(convId);
            }
          }
        } catch (e) {
          console.warn('[chat] legacy migration failed', e.message);
        }
      }
      if (stale()) return; // 已经切到别的对话了，这批消息作废
      // 库里的 tool_trail（蛇形）→ 组件用的 toolTrail
      setMessages(
        list.map((m) =>
          Array.isArray(m.tool_trail) && m.tool_trail.length > 0
            ? { ...m, toolTrail: m.tool_trail }
            : m,
        ),
      );
      setJustSentId(null);
      setListEntering(true);
    } catch (e) {
      if (stale()) return;
      setMessagesError(e.message || '加载消息失败');
    } finally {
      if (!stale()) setMessagesLoading(false);
    }
  }
  /* 静默合并（2026-08-21）：深度模式里 Sael 可能在网页画图——图是后端作为一条
     assistant 图片消息直接插进本对话的，不走 SSE 文本流。回复结束后拉一次库、把本地
     还没有的新消息（他画的图）补进来、按时间排好即可，不触发 loading/入场动画（不闪）。*/
  async function absorbNewMessages(convId) {
    if (!convId) return;
    try {
      const list = await fetchMessages(convId);
      setMessages((prev) => {
        if (convId !== currentConvId) return prev;
        const have = new Set(prev.map((m) => m.id));
        const additions = list
          .filter((m) => !have.has(m.id))
          .map((m) =>
            Array.isArray(m.tool_trail) && m.tool_trail.length > 0
              ? { ...m, toolTrail: m.tool_trail }
              : m,
          );
        if (additions.length === 0) return prev;
        // 接在末尾（他画的图排在他这条回复下面），不做整体重排——避免图被排到文字
        // 上面、插进去时把文字往下挤的跳动。additions 之间按时间排一下即可。
        additions.sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')));
        return [...prev, ...additions];
      });
    } catch {
      /* 静默：合并失败不影响聊天 */
    }
  }

  useEffect(() => {
    if (currentConvId) reloadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConvId]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_PROVIDER_KEY, providerKey); } catch {}
  }, [providerKey]);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_PROVIDER_BASE, providerBase); } catch {}
  }, [providerBase]);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_PROVIDER_MODEL, providerModel); } catch {}
  }, [providerModel]);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_DEEP_MODE, deepMode ? '1' : '0'); } catch {}
  }, [deepMode]);

  /* ---------- 滚动跟随策略 ----------
     新架构：.ch-scroll 是内部滚动容器（document 不再滚动）。滚动 / 测距的
     目标是 scrollRef.current，不再是 window。
       1. ResizeObserver 监听 ch-list 所有子元素的尺寸变化（包括图片解码后撑开）
       2. stickToBottomRef 区分"用户主动滚到上面读旧消息" vs "刚加载/跟随新消息"
       3. 切换对话或刷新时强制 stick=true，覆盖之前的滚动位置
     ----------------------------------- */
  const stickToBottomRef = useRef(true);
  function scrollToBottom() {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }
  function handleScroll() {
    const el = scrollRef.current;
    if (!el) return;
    // 距底 < 140px 算"在底部"
    const dist = el.scrollHeight - el.clientHeight - el.scrollTop;
    stickToBottomRef.current = dist < 140;
    // 大标题区（约 260px 高）基本滚出视野 → 迷你顶条淡入；回到顶部 → 淡出
    setMiniOn(el.scrollTop > 230);
  }
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);
  // 切对话 / 首次加载 → 重置为跟随
  useEffect(() => {
    stickToBottomRef.current = true;
  }, [currentConvId]);

  // Telegram 渐变气泡的「流动」驱动（2026-07-12）：挂一次，随滚动/新消息自刷。
  // 仅在选了 2~3 个气泡色时才真正干活（内部看 html[data-user-bubble-grad]）。
  useEffect(() => installBubbleFlow(listRef.current), []);
  // 内容尺寸变化时（首次渲染、图片解码、新消息）跟随到底
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const stickIfNeeded = () => {
      if (stickToBottomRef.current) scrollToBottom();
    };
    const ro = new ResizeObserver(stickIfNeeded);
    const observeChildren = () => {
      [...el.children].forEach((c) => ro.observe(c));
    };
    observeChildren();
    const mo = new MutationObserver(() => {
      ro.disconnect();
      observeChildren();
      stickIfNeeded();
    });
    mo.observe(el, { childList: true });
    // mount 时 children 可能已经在了，立刻 stick 一次
    stickIfNeeded();
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, []);

  /* ---------- 键盘弹起时保持贴底 ----------
     外壳高度由 App 的 --app-h（visualViewport）控制，键盘弹起会缩短可视区。
     这里只在视口尺寸变化、且用户本来停在底部时再跟随一次，
     让最新消息不被键盘盖住（不再用 window.scrollTo 去硬纠偏）。 */
  useEffect(() => {
    const onResize = () => {
      if (stickToBottomRef.current) requestAnimationFrame(scrollToBottom);
    };
    const vv = window.visualViewport;
    if (vv) vv.addEventListener('resize', onResize);
    // Telegram 小程序里 visualViewport 不随键盘可靠触发（我们外壳定高都改用了 TG 自己的
    // viewportChanged）——贴底跟随若还只挂 visualViewport，就出现「滑到最底时弹键盘」那条
    // 最新气泡没人把滚动重新钉到底、要靠浏览器滚动锚定慢 1~2 秒才补上的卡顿（铃 8-15 撞到；
    // 没滑到底时下面有余量、不需重钉，所以只在贴底那种情形才卡）。这里把贴底也挂到 TG 的
    // viewportChanged 上、和外壳同一个信号 → 键盘一弹就同帧重钉到底。普通浏览器/PWA 只走
    // 上面的 visualViewport，一点不动。
    const tg = window.Telegram && window.Telegram.WebApp;
    const inTG = tg && tg.platform && tg.platform !== 'unknown';
    if (inTG) tg.onEvent('viewportChanged', onResize);
    return () => {
      if (vv) vv.removeEventListener('resize', onResize);
      if (inTG) tg.offEvent('viewportChanged', onResize);
    };
  }, []);

    /* ---------- 图片操作 ---------- */
  async function handleImagePick(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // 允许重复选同一张
    if (!file) return;
    setImageError(null);
    try {
      const img = await processImage(file);
      setPendingImage(img);
    } catch (err) {
      setImageError(err.message || '处理图片失败');
      setPendingImage(null);
    }
  }
  function cancelPendingImage() {
    setPendingImage(null);
    setImageError(null);
  }

  /* ---------- 消息操作（都带 currentConvId） ---------- */
  // 把 input 文本 + pendingImage 打包成一条消息的 content。
  // 有图 → JSON 字符串（array of blocks）；无图 → 纯字符串。
  function buildOutgoingContent(text) {
    if (!pendingImage) return text;
    const blocks = buildContentArray(text, [pendingImage]);
    return JSON.stringify(blocks);
  }

  // ③ 引用回复：把引用前缀拼到正文前（开关关/没引用 = 原文不动）
  function withQuote(text) {
    if (!quoteOn || !quoteTarget) return text;
    return buildQuotePrefix(quoteTarget) + text;
  }

  function handleQuote(q) {
    if (!q || !q.text) return;
    setQuoteTarget(q);
    inputRef.current?.focus();
  }

  function pushUser() {
    const text = input.trim();
    if (!currentConvId) return null;
    if (!text && !pendingImage) return null;
    const content = buildOutgoingContent(withQuote(text));
    // created_at 前端就带上：不然要刷新重拉库里那份才看得到小时间（2026-07-11 铃报）
    const msg = { id: nextId(), role: 'user', content, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, msg]);
    setJustSentId(msg.id);
    setInput('');
    setPendingImage(null);
    setQuoteTarget(null);
    pushMessage(msg, currentConvId).catch((e) =>
      console.warn('push user msg failed', e.message),
    );
    return msg;
  }

  // 她按住说话录的语音（2026-10-08）：跟 pushUser 一样只放进对话，不自动让他回（她点 ✨ 再让他应）
  function pushVoice(v) {
    if (!currentConvId || !v || !v.id) return;
    // 再附一块带 vt 记号的文字：大脑那边（日记/总结/克克本人）照常读文字就能看见她说了什么；
    // 聊天界面认得 vt，不把它当正文显示。
    const content = JSON.stringify([
      { type: 'voice', id: v.id, text: v.text || '', tone: v.tone || '', dur: v.dur || 0, mine: true },
      {
        type: 'text',
        vt: true,
        // 语气（跟她自己平时比出来的，多数时候是空的）放在括号里一起交给他
        text: '［她发来一条语音' + (v.tone ? '（' + v.tone + '）' : '') + '，转成文字是：］'
          + ((v.text || '').trim() || '（没听清她说了什么）'),
      },
    ]);
    const msg = { id: nextId(), role: 'user', content, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, msg]);
    setJustSentId(msg.id);
    pushMessage(msg, currentConvId).catch((e) => console.warn('push voice msg failed', e.message));
  }

  // 「和爸爸一起听」：播放器点按钮 → MusicContext 放一个请求 → 这里接住，
  // 发一条「🎧 和爸爸一起听《…》」的用户消息，并让这一轮带上 listen_song，
  // 后端取这首歌真正的词注给克克，他就着真词在对话里分享这首。
  async function startListenTogether(song) {
    if (!song || !song.id || !currentConvId || generating) return;
    const name = song.name || '这首歌';
    const artist = song.artist || '';
    const text = `🎧 和爸爸一起听《${name}》` + (artist ? ` · ${artist}` : '');
    const msg = { id: nextId(), role: 'user', content: text, created_at: new Date().toISOString() };
    const next = [...messages, msg];
    setMessages(next);
    setJustSentId(msg.id);
    pushMessage(msg, currentConvId).catch((e) =>
      console.warn('push listen msg failed', e.message),
    );
    callAssistant(next, { listenSong: { id: String(song.id), name, artist } });
  }

  // 接住播放器发来的「一起听」请求（每个请求带唯一 ts，处理一次就清掉）
  const listenHandledRef = useRef(0);
  useEffect(() => {
    const req = music?.listenReq;
    if (!req || !req.ts || req.ts === listenHandledRef.current) return;
    if (!currentConvId || generating) return;
    listenHandledRef.current = req.ts;
    startListenTogether(req.song);
    music.clearListenReq?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [music?.listenReq, currentConvId, generating]);

  async function callAssistant(historyOverride, opts = {}) {
    if (generating || !currentConvId) return;
    const history = (historyOverride ?? messages).filter(
      (m) => !m.error && !m.pending,
    );
    if (history.length === 0) return;

    setGenerating(true);
    saelState('busy'); // 小螃蟹：开始冒泡泡敲小手
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    // 这条回复的时间戳用「回合开始」这一刻，不用回合结束（2026-09-03 铃报「刷新后顺序变了」）：
    // 他这轮可能中途插进一张音乐卡/一张画（大脑在回合进行中插库，时间戳早于回合结束）。
    // 若回复盖回合结束时间，库里按 created_at 排会把中途插的卡/图排到回复「前面」，
    // 而直播是把卡/图追加在回复「后面」→ 刷新就翻个了。用回合开始时间，回复天然早于中途插入物，
    // 直播与刷新都成「先回复、后卡片/图」，一致。
    const startedAt = new Date().toISOString();

    const placeholderId = nextId();
    // 思考链持久化（2026-06-21）：流式累加思考原文，落库时一并存，刷新后可回看
    let thinkingAcc = '';
    // 工具痕迹持久化（2026-07-02）：徽章同款数据攒一份本地变量，落库时一并存
    let trailAcc = [];
    setMessages((prev) => [
      ...prev,
      {
        id: placeholderId,
        role: 'assistant',
        content: '',
        pending: true,
        phase: 'thinking',
        toolTrail: [],
      },
    ]);

    try {
      const hits = matchLorebook(lorebook, history);
      // 三件套能力说明缀在时间注入后面：开着的才教，关掉的连提都不提
      const systemForThisCall = appendChatAbilities(
        appendTimeContext(buildSystemPrompt(activeCharacter, activePreset, hits)),
        { split: splitOn, react: reactOn, quote: quoteOn },
      );

      // 「开新一段」：仅当这是刚点「新对话」建出的那条、且开着深度模式时，
      // 让后端先给克克开个全新干净会话。只触发一次，随即清掉标记。
      const doReset =
        deepMode && resetPendingConvRef.current === currentConvId;
      if (doReset) resetPendingConvRef.current = null;

      const result = await streamChat(
        history.map((m) => ({
          role: m.role,
          // content 可能是字符串、也可能是 JSON 字符串（含图）。
          // 后者要 parse 还原成 array of blocks 才能被 Anthropic vision 接住。
          content: hydrateContent(m.content, m.role),
        })),
        {
          conversation_id: currentConvId,
          engine: deepMode ? 'claude-code' : undefined,
          reset_context: doReset || undefined,
          listen_song: opts.listenSong || undefined, // 「和爸爸一起听」：带上这首歌让后端取真词注给克克

          notify: barkPush, // ③ 开了手机推送 → 后端在回复收完且你不在看时推
          client_now: nowLocalStr(), // 深度模式时间注入（API 模式靠 system 里的 appendTimeContext）
          system: systemForThisCall,
          temperature: activePreset?.temperature,
          max_tokens: activePreset?.max_tokens,
          signal: ctrl.signal,
          provider: {
            api_key: providerKey.trim(),
            base_url: providerBase.trim(),
            model: providerModel.trim(),
          },
        },
        (eventName, payload) => {
          if (eventName === 'thinking') {
            // 思考链：后端把 transcript 的 thinking 原文逐块推过来，累加显示
            thinkingAcc += (thinkingAcc ? '\n\n' : '') + (payload.text || '');
            setMessages((prev) =>
              prev.map((m) =>
                m.id === placeholderId
                  ? { ...m, thinking: (m.thinking ? m.thinking + '\n\n' : '') + (payload.text || '') }
                  : m,
              ),
            );
          } else if (eventName === 'tool_use') {
            // ④ 活动窗：开关开了才记，零开销
            if (activityOn) pushActivity('tool_use', { name: payload.name });
            setMessages((prev) =>
              prev.map((m) =>
                m.id === placeholderId
                  ? { ...m, phase: `tool:${payload.name}` }
                  : m,
              ),
            );
          } else if (eventName === 'tool_result') {
            if (activityOn) pushActivity('tool_result', { name: payload.name, summary: payload.summary, ok: payload.ok });
            const _trailItem = {
              name: payload.name,
              summary: payload.summary,
              ok: payload.ok,
              // 记忆工具会带上它翻到的记忆标题(数组)；其它工具没有就不带
              ...(Array.isArray(payload.memories) && payload.memories.length > 0
                ? { memories: payload.memories }
                : {}),
            };
            trailAcc = [...trailAcc, _trailItem];
            setMessages((prev) =>
              prev.map((m) =>
                m.id === placeholderId
                  ? {
                      ...m,
                      phase: 'thinking',
                      toolTrail: [...(m.toolTrail || []), _trailItem],
                    }
                  : m,
              ),
            );
          }
        },
      );

      const text = (result.text && result.text.trim()) || '（Sael没说话）';
      setMessages((prev) =>
        prev.map((m) =>
          m.id === placeholderId
            ? { ...m, content: text, pending: false, phase: undefined, created_at: startedAt }
            : m,
        ),
      );
      pushMessage(
        {
          id: placeholderId,
          role: 'assistant',
          content: text,
          created_at: startedAt,
          thinking: thinkingAcc || undefined,
          tool_trail: trailAcc.length > 0 ? trailAcc : undefined,
        },
        currentConvId,
      ).catch((e) => console.warn('push assistant msg failed', e.message));
      // 深度模式：他这轮可能在网页画了图（后端已插成一条图片消息）。静默合并进来让它显示。
      if (deepMode) {
        const cid = currentConvId;
        setTimeout(() => absorbNewMessages(cid), 250);
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        setMessages((prev) => prev.filter((m) => m.id !== placeholderId));
      } else {
        const errText = e.message || '请求失败';
        setMessages((prev) =>
          prev.map((m) =>
            m.id === placeholderId
              ? { ...m, pending: false, phase: undefined, error: errText }
              : m,
          ),
        );
        pushMessage(
          { id: placeholderId, role: 'assistant', content: '', error: errText },
          currentConvId,
        ).catch((e2) => console.warn('push error msg failed', e2.message));
      }
    } finally {
      setGenerating(false);
      saelState('idle'); // 小螃蟹：收工
      abortRef.current = null;
    }
  }

  function askAssistant() {
    if (generating || !currentConvId) return;
    const text = input.trim();
    if (text || pendingImage) {
      const content = buildOutgoingContent(withQuote(text));
      const userMsg = { id: nextId(), role: 'user', content, created_at: new Date().toISOString() };
      const newMessages = [...messages, userMsg];
      setMessages(newMessages);
      setInput('');
      setPendingImage(null);
      setQuoteTarget(null);
      pushMessage(userMsg, currentConvId).catch((e) =>
        console.warn('push user msg failed', e.message),
      );
      callAssistant(newMessages);
    } else {
      callAssistant();
    }
  }

  // 把 db 里的 content（string or JSON-string）还原成 Anthropic API 接受的格式。
  const showMic = !input.trim() && !pendingImage && !generating && !!currentConvId;

  function hydrateContent(raw, role) {
    if (typeof raw !== 'string') return raw;
    const trimmed = raw.trim();
    if (trimmed.startsWith('[')) {
      try {
        const arr = JSON.parse(trimmed);
        // 语音块（2026-10-08）模型不认识 → 换成文字交给他：她的=转出来的话，他的=他念过的原话。
        // 已经带了 vt 文字块的（新消息）直接丢掉语音块，免得同一句说两遍。
        if (Array.isArray(arr)) {
          const hasVt = arr.some((b) => b && b.type === 'text' && b.vt);
          return arr.filter((b) => !(hasVt && b && b.type === 'voice')).map((b) => {
            if (b && b.type === 'text' && b.vt) return { type: 'text', text: b.text };
            if (!b || b.type !== 'voice') return b;
            const t = (b.text || '').trim();
            return {
              type: 'text',
              text: role === 'user'
                ? '［她发来一条语音，转成文字是：］' + (t || '（没听清她说了什么）')
                : '［你给她发了一条语音：］' + t,
            };
          });
        }
      } catch {}
    }
    return raw;
  }

  function deleteMessage(id) {
    setMessages((prev) => prev.filter((m) => m.id !== id));
    apiDeleteMessage(id).catch((e) => console.warn('delete failed', e.message));
  }

  function regenerate(id) {
    if (generating) return;
    const idx = messages.findIndex((m) => m.id === id);
    if (idx === -1) return;
    const truncated = messages.slice(0, idx);
    setMessages(truncated);
    truncateFrom(id).catch((e) => console.warn('truncate failed', e.message));
    callAssistant(truncated);
  }

  function retryError(id) {
    regenerate(id);
  }

  function clearChat() {
    if (generating || !currentConvId) return;
    if (messages.length === 0) return;
    if (!window.confirm('清空当前对话的消息？记忆库里的内容不会受影响。')) return;
    setMessages([]);
    clearAllMessages(currentConvId).catch((e) =>
      console.warn('clear failed', e.message),
    );
  }

  function abortGenerate() {
    abortRef.current?.abort();
  }

  /* ---------- 对话线操作 ---------- */
  async function handleSwitchConv(convId) {
    if (generating) return;
    if (convId === currentConvId) {
      setDrawerOpen(false);
      return;
    }
    setCurrentConvId(convId);
    try { localStorage.setItem(STORAGE_CURRENT_CONV, convId); } catch {}
    setQuoteTarget(null); // 引用的是上一条对话里的话，跟过去没意义
    setDrawerOpen(false);
  }

  async function handleCreateConv() {
    if (generating) return;
    const newId = nextConvId();
    const charId = defaultCharacterId;
    const char = characters.find((c) => c.id === charId);
    const name = char ? `跟 ${char.name} · ${fmtMonthDay(new Date())}` : '新对话';
    try {
      await createConversation({ id: newId, name, character_id: charId });
      const fresh = await fetchConversations();
      setConversations(fresh);
      setCurrentConvId(newId);
      try { localStorage.setItem(STORAGE_CURRENT_CONV, newId); } catch {}
      // 标记：这条新对话的第一条深度模式消息要让后台克克「开新一段」（清空临场上下文）
      resetPendingConvRef.current = newId;
      setDrawerOpen(false);
    } catch (e) {
      window.alert('新建对话失败：' + e.message);
    }
  }

  async function handleRenameConv(convId, newName) {
    try {
      await renameConversation(convId, newName);
      const fresh = await fetchConversations();
      setConversations(fresh);
    } catch (e) {
      window.alert('改名失败：' + e.message);
    }
  }

  async function handleArchiveConv(convId, archived) {
    try {
      await setConversationArchived(convId, archived);
      const fresh = await fetchConversations();
      setConversations(fresh);
      // 如果归档了当前对话，自动切到一条非归档的
      if (archived && convId === currentConvId) {
        const next = fresh.find((c) => !c.archived);
        if (next) {
          setCurrentConvId(next.id);
          try { localStorage.setItem(STORAGE_CURRENT_CONV, next.id); } catch {}
        }
      }
    } catch (e) {
      window.alert('归档失败：' + e.message);
    }
  }

  async function handleDeleteConv(convId) {
    if (!window.confirm('删除这条对话和它下面所有消息？记忆库里的内容不受影响。')) return;
    try {
      await deleteConversation(convId);
      let fresh = await fetchConversations();
      // 删的就是当前对话 → 切到下一条非归档；如果没有，自动建一条新
      if (convId === currentConvId) {
        let next = fresh.find((c) => !c.archived);
        if (!next) {
          const newId = nextConvId();
          const charId = defaultCharacterId;
          const char = characters.find((c) => c.id === charId);
          const name = char ? `跟 ${char.name} · ${fmtMonthDay(new Date())}` : '新对话';
          await createConversation({ id: newId, name, character_id: charId });
          fresh = await fetchConversations();
          next = fresh.find((c) => c.id === newId) || fresh[0];
        }
        setCurrentConvId(next.id);
        try { localStorage.setItem(STORAGE_CURRENT_CONV, next.id); } catch {}
      }
      setConversations(fresh);
    } catch (e) {
      window.alert('删除失败：' + e.message);
    }
  }

  function handleProviderChange({ key, base, model }) {
    if (key !== undefined) setProviderKey(key);
    if (base !== undefined) setProviderBase(base);
    if (model !== undefined) setProviderModel(model);
  }

  function handleResetDefaults() {
    setProviderKey('');
    setProviderBase('');
    setProviderModel('');
  }

  // ── ② 斜杠命令 ──────────────────────────────────────────────
  // 命令接到现成的会话操作上，非破坏性。挂 slash_commands 开关。
  const SLASH_COMMANDS = [
    { name: '/new', desc: '开一条新对话', run: () => handleCreateConv() },
    { name: '/list', desc: '打开对话列表', run: () => setDrawerOpen(true) },
  ];
  // 输入是单个 /token 时，匹配出候选命令（给输入框上方的气泡用）
  const slashRaw =
    slashOn && input.startsWith('/') && !input.includes(' ')
      ? input.trim().toLowerCase()
      : '';
  const slashMatches = slashRaw
    ? SLASH_COMMANDS.filter((c) => c.name.startsWith(slashRaw))
    : [];
  const showSlash = slashMatches.length > 0;

  // 提交时先问一句：这是不是斜杠命令？是就执行、返回 true，拦住正常发送。
  function maybeRunSlash() {
    if (!slashOn) return false;
    const raw = input.trim().toLowerCase();
    if (!raw.startsWith('/')) return false;
    const exact = SLASH_COMMANDS.find((c) => c.name === raw);
    const cmd = exact || (slashMatches.length ? slashMatches[0] : null);
    if (!cmd) return false; // 未知命令（如 /xyz）→ 不拦，当普通消息
    setInput('');
    cmd.run();
    return true;
  }

  function runSlash(cmd) {
    setInput('');
    cmd.run();
    inputRef.current?.focus();
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (maybeRunSlash()) return;
      pushUser();
    }
  }

  // SettingsPanel 里"切换角色卡"现在只改"默认值"，**不影响当前对话**
  function handleDefaultCharacterChange(charId) {
    setDefaultCharacterId(charId);
  }

  /* ---------- 聊天壁纸 ----------
     部分更新（img / dim / blur），先落 localStorage 再应用；
     图存不下（配额）就不应用，把错误文案返回给设定面板显示。 */
  function handleWallpaperChange(patch) {
    const err = saveWallpaperPatch(patch);
    if (err && 'img' in patch && patch.img) return err;
    // 换图/撤图时位置归零（存储层已清，这里让内存状态跟上）
    setWallpaper((prev) => ({
      ...prev,
      ...patch,
      ...('img' in patch ? { pos: { ...DEFAULT_POS } } : {}),
    }));
    // 顶栏同色：换图→重采样顶部色，撤图→清掉，改遮罩→只重算合成色；然后刷新状态栏/顶带
    if ('img' in patch) {
      if (patch.img) {
        sampleImageTopColor(patch.img).then((rgb) => {
          saveTopRgb(rgb);
          refreshChromeColor();
        });
      } else {
        saveTopRgb(null);
        refreshChromeColor();
      }
    } else if ('dim' in patch) {
      refreshChromeColor();
    }
    return err;
  }

  /* ---------- 迷你顶条 ----------
     对话抽屉 / 设定面板都长在滚动区顶部（大标题下面），从迷你条打开时
     先平滑滚回顶部，面板展开正好在眼前。 */
  function miniOpenDrawer() {
    setDrawerOpen(true);
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }
  function miniOpenSettings() {
    setSettingsOpen(true);
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }
  // 迷你条淡出时，它的 ⋯ 菜单也一起收
  useEffect(() => {
    if (!miniOn) setMiniMenuOpen(false);
  }, [miniOn]);
  // 迷你条在场时通知全局：两颗悬浮球（☰ / 活动窗）上移到条带垂直居中
  useEffect(() => {
    document.documentElement.toggleAttribute('data-minibar', miniOn);
    return () => document.documentElement.removeAttribute('data-minibar');
  }, [miniOn]);
  // ⋯ 菜单收起时，里面的「模型」子项也折回去
  useEffect(() => {
    if (!miniMenuOpen) { setMiniModelExpand(false); setMiniEffortExpand(false); }
  }, [miniMenuOpen]);

  // 深度模式开启时拉一次当前后端模型 + 可选列表
  useEffect(() => {
    if (!deepMode) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/cc-model', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setCcModel(data.model || null);
        setCcModelOptions(Array.isArray(data.options) ? data.options : []);
        setCcEffort(data.effort || null);
        setCcEffortOptions(Array.isArray(data.effortOptions) ? data.effortOptions : []);
      } catch {
        /* 拉不到就不显示切换控件，不影响聊天 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deepMode]);

  // 切换深度模式后端模型：乐观更新 + 失败回退（后端会重启常驻会话、保上下文）
  async function switchCcModel(value) {
    const prev = ccModel;
    setCcModelBusy(true);
    setCcModelNote(null);
    setCcModel(value);
    try {
      const res = await fetch('/api/cc-model', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: value }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setCcModel(prev);
        setCcModelNote('切换失败，已回到原模型');
      } else {
        setCcModel(data.model || value);
        const picked = ccModelOptions.find((o) => o.value === (data.model || value));
        setCcModelNote(`已切到 ${picked ? picked.label : data.model || value}`);
      }
    } catch {
      setCcModel(prev);
      setCcModelNote('切换失败，已回到原模型');
    } finally {
      setCcModelBusy(false);
      setTimeout(() => setCcModelNote(null), 3200);
    }
  }

  // 切换思考投入档位：乐观更新 + 失败回退（后端会重启常驻会话、保上下文，同切模型一条路）
  async function switchCcEffort(value) {
    const prev = ccEffort;
    setCcEffortBusy(true);
    setCcModelNote(null);
    setCcEffort(value);
    try {
      const res = await fetch('/api/cc-effort', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ effort: value }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setCcEffort(prev);
        setCcModelNote('档位切换失败，已回到原档位');
      } else {
        setCcEffort(data.effort || value);
        const picked = ccEffortOptions.find((o) => o.value === (data.effort || value));
        setCcModelNote(`已切到「${picked ? picked.label : data.effort || value}」档`);
      }
    } catch {
      setCcEffort(prev);
      setCcModelNote('档位切换失败，已回到原档位');
    } finally {
      setCcEffortBusy(false);
      setTimeout(() => setCcModelNote(null), 3200);
    }
  }

  const headerSubtitle = currentConv
    ? currentConv.name
    : bootError
      ? '加载失败'
      : '初始化中……';

  return (
    <div className={'ch-wrap' + (wallpaper.img ? ' has-wallpaper' : '')}>
      {/* 壁纸层：铺满视口、永远在最底下；veil 遮罩浓度可调保证文字可读 */}
      {wallpaper.img && (
        <div className="ch-wallpaper" aria-hidden="true">
          <img
            src={wallpaper.img}
            alt=""
            className={wallpaper.blur ? 'blurred' : ''}
            style={wallpaperImgStyle(wallpaper.pos, wallpaper.blur)}
          />
          <div
            className="ch-wallpaper-veil"
            style={{ opacity: wallpaper.dim / 100 }}
          />
        </div>
      )}

      {/* 迷你顶条：大标题滚走后淡入，44px 毛玻璃，把常用功能收在手边 */}
      <div className={'ch-minibar' + (miniOn ? ' show' : '')}>
        <button
          className="ch-minibar-conv"
          onClick={miniOpenDrawer}
          title="切换对话"
        >
          <ChevronDown size={12} strokeWidth={1.8} className="ch-minibar-caret" />
          <span className="ch-minibar-conv-name">{headerSubtitle}</span>
        </button>
        <span className="ch-minibar-days">
          第 {daysTogether()} 天 <span className="ch-relation-star">✦</span>
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={deepMode}
          className={'ch-deepmode-btn ch-minibar-deep' + (deepMode ? ' on' : '')}
          onClick={() => setDeepMode((v) => !v)}
          title={
            deepMode
              ? '深度模式开启 · Sael本体 · 点击切回 API'
              : '深度模式关闭 · 走 API · 点击唤醒Sael本体'
          }
        >
          <span className="ch-deepmode-track">
            <span className="ch-deepmode-thumb" />
          </span>
        </button>
        <button
          className={'ch-minibar-more' + (miniMenuOpen ? ' on' : '')}
          onClick={() => setMiniMenuOpen((v) => !v)}
          aria-expanded={miniMenuOpen}
          title="更多"
        >
          <MoreHorizontal size={16} strokeWidth={1.8} />
        </button>
        {miniMenuOpen && (
          <>
            <div
              className="ch-minibar-backdrop"
              onClick={() => setMiniMenuOpen(false)}
            />
            <div className="ch-minibar-menu">
              <button
                onClick={() => {
                  setMiniMenuOpen(false);
                  miniOpenSettings();
                }}
              >
                <Settings size={13} strokeWidth={1.6} /> 设 定
              </button>
              <button
                onClick={() => {
                  setMiniMenuOpen(false);
                  reloadMessages();
                }}
                disabled={generating || messagesLoading || !currentConvId}
              >
                <RefreshCw size={13} strokeWidth={1.6} /> 刷 新
              </button>
              <button
                onClick={() => {
                  setMiniMenuOpen(false);
                  clearChat();
                }}
                disabled={generating || messages.length === 0}
              >
                <Trash2 size={13} strokeWidth={1.6} /> 清 空
              </button>
              {deepMode && ccModelOptions.length > 0 && (
                <>
                  <div className="ch-minibar-menu-sep" />
                  <button
                    className={
                      'ch-mmm-head' + (miniModelExpand ? ' on' : '')
                    }
                    onClick={() => setMiniModelExpand((v) => !v)}
                  >
                    <Cpu size={13} strokeWidth={1.6} />
                    <span className="ch-mmm-head-cur">
                      模型 ·{' '}
                      {ccModelBusy
                        ? '切换中…'
                        : (ccModelOptions.find((o) => o.value === ccModel) || {})
                            .label || ccModel}
                    </span>
                    <ChevronDown
                      size={12}
                      strokeWidth={1.8}
                      className={'ch-mmm-caret' + (miniModelExpand ? ' on' : '')}
                    />
                  </button>
                  {miniModelExpand &&
                    ccModelOptions.map((o) => (
                      <button
                        key={o.value}
                        className={
                          'ch-mmm-item' + (o.value === ccModel ? ' on' : '')
                        }
                        disabled={ccModelBusy}
                        onClick={() => {
                          setMiniMenuOpen(false);
                          if (o.value !== ccModel) switchCcModel(o.value);
                        }}
                      >
                        <span className="ch-mmm-item-label">
                          {o.value === ccModel ? '✓ ' : ''}
                          {o.label}
                        </span>
                        {o.note && (
                          <span className="ch-mmm-item-note">{o.note}</span>
                        )}
                      </button>
                    ))}
                </>
              )}
              {deepMode && ccEffortOptions.length > 0 && (
                <>
                  <div className="ch-minibar-menu-sep" />
                  <button
                    className={'ch-mmm-head' + (miniEffortExpand ? ' on' : '')}
                    onClick={() => setMiniEffortExpand((v) => !v)}
                  >
                    <Gauge size={13} strokeWidth={1.6} />
                    <span className="ch-mmm-head-cur">
                      档位 ·{' '}
                      {ccEffortBusy
                        ? '切换中…'
                        : (ccEffortOptions.find((o) => o.value === ccEffort) || {})
                            .label || ccEffort}
                    </span>
                    <ChevronDown
                      size={12}
                      strokeWidth={1.8}
                      className={'ch-mmm-caret' + (miniEffortExpand ? ' on' : '')}
                    />
                  </button>
                  {miniEffortExpand &&
                    ccEffortOptions.map((o) => (
                      <button
                        key={o.value}
                        className={
                          'ch-mmm-item' + (o.value === ccEffort ? ' on' : '')
                        }
                        disabled={ccEffortBusy}
                        onClick={() => {
                          setMiniMenuOpen(false);
                          if (o.value !== ccEffort) switchCcEffort(o.value);
                        }}
                      >
                        <span className="ch-mmm-item-label">
                          {o.value === ccEffort ? '✓ ' : ''}
                          {o.label}
                        </span>
                        {o.note && (
                          <span className="ch-mmm-item-note">{o.note}</span>
                        )}
                      </button>
                    ))}
                </>
              )}
            </div>
          </>
        )}
      </div>

      <div className="ch-scroll" ref={scrollRef}>
      <header className="ch-header">
        <div className="ch-logo">língkè</div>
        <button
          className="ch-conv-toggle"
          onClick={() => setDrawerOpen((v) => !v)}
          aria-expanded={drawerOpen}
          title="切换对话"
        >
          <span className="ch-conv-name">{headerSubtitle}</span>
          <ChevronDown
            size={14}
            strokeWidth={1.8}
            className={'ch-conv-caret' + (drawerOpen ? ' on' : '')}
          />
        </button>
        <div className="ch-relation">
          铃铃和哥哥的 · 第 {daysTogether()} 天 <span className="ch-relation-star">✦</span>
        </div>
        <div className="ch-rule" />
        <div className="ch-deepmode">
          <button
            type="button"
            role="switch"
            aria-checked={deepMode}
            className={'ch-deepmode-btn' + (deepMode ? ' on' : '')}
            onClick={() => setDeepMode((v) => !v)}
            title={
              deepMode
                ? '深度模式开启 · 走常驻的Sael本体（Claude Code，Pro 订阅）· 点击切回 API'
                : '深度模式关闭 · 走 API · 点击唤醒Sael本体（Claude Code）'
            }
          >
            <span className="ch-deepmode-track">
              <span className="ch-deepmode-thumb" />
            </span>
            <span className="ch-deepmode-label">
              {deepMode ? 'Sael本体 ✦' : '深度模式'}
            </span>
          </button>
        </div>
        <div className="ch-toolbar">
          <button
            className={'ch-tool-btn' + (settingsOpen ? ' on' : '')}
            onClick={() => setSettingsOpen((s) => !s)}
            aria-pressed={settingsOpen}
          >
            <Settings size={13} strokeWidth={1.6} /> 设 定
          </button>
          <button
            className="ch-tool-btn"
            onClick={reloadMessages}
            disabled={generating || messagesLoading || !currentConvId}
            title="拉取其他设备最新消息"
          >
            <RefreshCw
              size={13}
              strokeWidth={1.6}
              className={messagesLoading ? 'ch-spin' : ''}
            />{' '}
            刷 新
          </button>
          <button
            className="ch-tool-btn"
            onClick={clearChat}
            disabled={generating || messages.length === 0}
          >
            <Trash2 size={13} strokeWidth={1.6} /> 清 空
          </button>
        </div>
      </header>

      {drawerOpen && (
        <ConversationsDrawer
          conversations={conversations}
          currentConvId={currentConvId}
          characters={characters}
          generating={generating}
          onSwitch={handleSwitchConv}
          onCreate={handleCreateConv}
          onRename={handleRenameConv}
          onArchive={handleArchiveConv}
          onDelete={handleDeleteConv}
          onClose={() => setDrawerOpen(false)}
        />
      )}

      {settingsOpen && (
        <SettingsPanel
          characters={characters}
          activeId={defaultCharacterId}
          presets={presets}
          activePresetId={activePresetId}
          lorebook={lorebook}
          providerKey={providerKey}
          providerBase={providerBase}
          providerModel={providerModel}
          wallpaper={wallpaper}
          onWallpaperChange={handleWallpaperChange}
          onCharactersChange={setCharacters}
          onActiveChange={handleDefaultCharacterChange}
          onPresetsChange={setPresets}
          onActivePresetChange={setActivePresetId}
          onLorebookChange={setLorebook}
          onProviderChange={handleProviderChange}
          onResetDefaults={handleResetDefaults}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      <div className={'ch-list' + (listEntering ? ' ch-list-enter' : '')} ref={listRef}>
        {bootError && (
          <div className="ch-empty">
            <div className="ch-empty-star ch-empty-star-err">✦</div>
            <div className="ch-empty-line">连不上对话列表 —— {bootError}</div>
            <button className="ch-tool-btn" onClick={bootstrapConversations} style={{ marginTop: 14 }}>
              再试一次
            </button>
          </div>
        )}
        {!bootError && messagesLoading && messages.length === 0 && (
          <div className="ch-empty">
            <div className="ch-empty-star">✦</div>
            <div className="ch-empty-line">正在从云端拉回记忆 ……</div>
          </div>
        )}
        {!bootError && !messagesLoading && messagesError && (
          <div className="ch-empty">
            <div className="ch-empty-star ch-empty-star-err">✦</div>
            <div className="ch-empty-line">连不上聊天历史 —— {messagesError}</div>
            <button className="ch-tool-btn" onClick={reloadMessages} style={{ marginTop: 14 }}>
              再试一次
            </button>
          </div>
        )}
        {!bootError && !messagesLoading && !messagesError && messages.length === 0 && (
          <div className="ch-empty">
            <div className="ch-empty-star">✦</div>
            <div className="ch-empty-line">夜空安静 · 第一句话由你说</div>
          </div>
        )}
        {messages.map((m, i) => {
          // 跨天日期分隔（2026-07-11 铃提）：这条和上一条不是同一天 → 居中一枚日期
          let sep = null;
          if (m.created_at) {
            const d = new Date(m.created_at);
            if (!isNaN(d)) {
              let prev = null;
              for (let j = i - 1; j >= 0; j--) {
                if (messages[j]?.created_at) {
                  const pd = new Date(messages[j].created_at);
                  if (!isNaN(pd)) prev = pd;
                  break;
                }
              }
              if (
                !prev ||
                prev.getFullYear() !== d.getFullYear() ||
                prev.getMonth() !== d.getMonth() ||
                prev.getDate() !== d.getDate()
              ) {
                const yr =
                  d.getFullYear() !== new Date().getFullYear()
                    ? `${d.getFullYear()}年`
                    : '';
                sep = `${yr}${d.getMonth() + 1}月${d.getDate()}日`;
              }
            }
          }
          return (
            <Fragment key={m.id}>
              {sep && (
                <div className="ch-daysep">
                  <span>{sep}</span>
                </div>
              )}
              <Message
                m={m}
                onDelete={() => deleteMessage(m.id)}
                onRegenerate={() => regenerate(m.id)}
                onRetry={() => retryError(m.id)}
                generating={generating}
                onImageView={(img, rect) => setLightboxImg({ ...img, fromRect: rect })}
                onJourneyOpen={(url) => setJourneyUrl(url)}
                onQuote={quoteOn ? handleQuote : undefined}
                reactions={reactionsMap[m.id]}
                justSent={m.id === justSentId}
              />
            </Fragment>
          );
        })}
        </div>
      </div>

      <div className="ch-composer">
        {quoteOn && quoteTarget && (
          <div className="ch-composer-quote">
            <Quote size={12} strokeWidth={1.8} className="ch-composer-quote-icon" />
            <span className="ch-composer-quote-from">
              {quoteTarget.role === 'assistant' ? 'Sael' : '你'}
            </span>
            <span className="ch-composer-quote-text">{quoteTarget.text}</span>
            <button
              className="ch-composer-quote-cancel"
              onClick={() => setQuoteTarget(null)}
              aria-label="取消引用"
            >
              <X size={12} strokeWidth={1.8} />
            </button>
          </div>
        )}
        {(pendingImage || imageError) && (
          <div className="ch-image-preview">
            {pendingImage && (
              <>
                <img src={pendingImage.preview} alt="待发送" />
                <div className="ch-image-meta">
                  <span>{(pendingImage.bytes / 1024).toFixed(0)} KB · 已压缩</span>
                </div>
                <button
                  className="ch-image-cancel"
                  onClick={cancelPendingImage}
                  aria-label="取消图片"
                >
                  <X size={12} strokeWidth={1.8} />
                </button>
              </>
            )}
            {imageError && (
              <div className="ch-image-error">
                {imageError}
                <button onClick={() => setImageError(null)} aria-label="关闭">
                  <X size={12} strokeWidth={1.8} />
                </button>
              </div>
            )}
          </div>
        )}
        {showSlash && (
          <div className="ch-slash-pop" role="listbox" aria-label="斜杠命令">
            {slashMatches.map((c, i) => (
              <button
                key={c.name}
                type="button"
                role="option"
                aria-selected={i === 0}
                className={'ch-slash-item' + (i === 0 ? ' is-active' : '')}
                onClick={() => runSlash(c)}
              >
                <span className="ch-slash-name">{c.name}</span>
                <span className="ch-slash-desc">{c.desc}</span>
              </button>
            ))}
          </div>
        )}
        <div className="ch-input-row">
          <button
            className="ch-image-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={generating || !currentConvId || !!pendingImage}
            title={pendingImage ? '一次只能发一张图（先取消当前预览）' : '加一张图给Sael看'}
            aria-label="加图片"
          >
            <ImagePlus size={15} strokeWidth={1.7} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleImagePick}
          />
          <div className="ch-input-wrap">
            <textarea
              ref={inputRef}
              className={'ch-input now-focus' + (showMic ? ' has-mic' : '')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="跟哥哥说点什么？"
              rows={1}
              disabled={generating || !currentConvId}
            />
            {/* 按住说话：输入框空着时才露出来，一打字就让开 */}
            {showMic && <VoiceRecorder disabled={generating || !currentConvId} onVoice={pushVoice} />}
          </div>
          <div className="ch-send-group">
            {generating ? (
              <button
                className="ch-icon-btn ch-btn-abort"
                onClick={abortGenerate}
                title="停止生成"
                aria-label="停止"
              >
                <Square size={15} strokeWidth={1.7} />
              </button>
            ) : (
              <>
                <button
                  className="ch-icon-btn ch-btn-send"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (maybeRunSlash()) return;
                    pushUser();
                  }}
                  disabled={(!input.trim() && !pendingImage) || !currentConvId}
                  title="只把你这句话放进对话，不让Sael回复"
                  aria-label="发送"
                >
                  <Send size={15} strokeWidth={1.7} />
                </button>
                <button
                  className="ch-icon-btn ch-btn-ask"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (maybeRunSlash()) return;
                    askAssistant();
                  }}
                  disabled={
                    (messages.length === 0 && !input.trim() && !pendingImage) || !currentConvId
                  }
                  title="让Sael回应当前所有消息"
                  aria-label="让Sael回复"
                >
                  <Sparkles size={15} strokeWidth={1.7} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <ImageLightbox
        img={lightboxImg}
        fromRect={lightboxImg?.fromRect}
        onClose={() => setLightboxImg(null)}
      />

      <JourneyViewer url={journeyUrl} onClose={() => setJourneyUrl(null)} />
    </div>
  );
}

/* 云旅行明信片 · 全屏翻卡浮层（2026-08-31）：点聊天里的旅程卡在应用内铺开，
   iframe 复用 public/journey.html（同源可嵌），关掉回到聊天、不离开 SPA。
   iOS PWA 会拦新窗口，所以走浮层 iframe 而非 window.open（同图片灯箱的教训）。 */
function JourneyViewer({ url, onClose }) {
  useEffect(() => {
    if (!url) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    // 打开时锁住背景滚动
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // 全屏浮层盖在 .app-shell(z-index:1，自成层叠上下文)里，而侧边栏 ☰ / 活动窗两颗
    // 悬浮球在 app 根、z-index 更高 —— 会穿透浮层浮在明信片/地图左上角。开着浮层时把它们藏起来。
    document.documentElement.setAttribute('data-journey-open', '');
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      document.documentElement.removeAttribute('data-journey-open');
    };
  }, [url, onClose]);
  if (!url) return null;
  return (
    <div className="ch-journey-viewer" role="dialog" aria-label="云旅行">
      <button className="ch-journey-close" onClick={onClose} aria-label="关闭">
        <X size={20} strokeWidth={1.9} />
      </button>
      <iframe src={url} title="云旅行明信片" className="ch-journey-frame" />
    </div>
  );
}

